import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Button, ButtonStyled } from '../../../button/button';

export interface DisabledState {
  tooltip: string;
}

interface Props {
  disabled?: DisabledState;
  logo: string;
  useBorderRadius?: boolean;
  title: string;
  description: React.ReactNode;
  onSelect: () => void;
}

const IntegrationCard = ({ useBorderRadius = true, ...props }: Props) => (
  <IntegrationCardInner useBorderRadius={useBorderRadius} {...props} />
);

const IntegrationCardInner = ({ useBorderRadius = true, ...props }: Props) => {
  const { t } = useTranslation('integrations');

  return (
    <Component aria-label={props.title} disabled={props.disabled}>
      <Logo src={props.logo} useBorderRadius={useBorderRadius} />

      <Content>
        <h4 className="notranslate">{props.title}</h4>
        <Description>{props.description}</Description>
      </Content>

      <Footer>
        <Button
          color="primary"
          disabled={!!props.disabled}
          title={props.disabled?.tooltip}
          variant="contained"
          onClick={props.onSelect}
        >
          {t('integrationList.card.addButton', { ns: 'integrations' })}
        </Button>
      </Footer>
    </Component>
  );
};

const Component = styled.article<Pick<Props, 'disabled'>>`
  display: flex;
  flex-direction: column;
  gap: 1em;

  width: 220px;
  padding: 1em;

  align-items: center;
  text-align: center;

  pointer-events: all;
  border-radius: 5px;

  box-shadow:
    0 1px 3px rgb(0 0 0 / 12%),
    0 1px 2px rgb(0 0 0 / 24%);
  background-color: #fafafa;

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        box-shadow:
          0 3px 6px rgba(0, 0, 0, 0.16),
          0 3px 6px rgba(0, 0, 0, 0.23);
        background-color: #f6f6f6;
      }
    `}
`;

const Logo = styled.img<Pick<Props, 'useBorderRadius'>>`
  height: 50px;
  max-width: 100%;
  ${(props) =>
    props.useBorderRadius &&
    css`
      border-radius: 1em;
    `}
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  h4 {
    font-size: 1rem;
    margin-bottom: 0.4em;
  }
`;

const Description = styled.p`
  font-size: 0.85rem;
  line-height: 1.4em;
  color: #555;
  margin: 0;
`;

const Footer = styled.footer`
  width: 100%;
  display: flex;
  justify-content: center;

  ${ButtonStyled} {
    min-width: 50%;
  }
`;

export { IntegrationCard };
